/**
 * Conversation history for the assistant (GET /assistant/conversations) —
 * the past + current threads shown in the planner sidebar and the drawer's
 * history view. Same plain imperative pattern as useAssistantInsights (no
 * SWR in the assistant area).
 *
 * `refresh()` refetches in place — call it after anything that changes the
 * list (reset, activate, a completed first turn that minted a title).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AssistantConversationSummaryDto,
  listAssistantConversations,
} from 'services/api/assistant';

export function useAssistantConversations({
  eventId,
  enabled,
}: {
  eventId: string | undefined;
  enabled: boolean;
}) {
  const [conversations, setConversations] = useState<AssistantConversationSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!enabled || !eventId) return;
    let cancelled = false;
    // Only the FIRST load shows a spinner — refreshes swap the data in place
    // so the list never flickers while a title arrives or a thread archives.
    setLoading((prev) => prev || fetchKey === 0);
    listAssistantConversations(eventId)
      .then((data) => {
        if (cancelled) return;
        setConversations(data.conversations);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, eventId, fetchKey]);

  const refresh = useCallback(() => setFetchKey((k) => k + 1), []);

  return { conversations, loading, error, refresh };
}
